import type { DiskContentUrl } from '../Contracts/DiskNode'

/**
 * Запас до кінця строку підписаного посилання: посилання, якому лишилося
 * менше, вже не видаємо — відео чи PDF, почавши з ним, можуть дочитувати
 * вміст ще хвилини.
 */
export const CONTENT_URL_MARGIN_MS = 5 * 60 * 1000

interface Entry {
    url: string
    /** Коли посилання перестає бути придатним (строк мінус запас), мс. */
    validUntil: number
}

/**
 * Кеш підписаних посилань `content_url` (`DISK-001` §4.6): посилання живе до
 * години, а переглядач просить його для кожного показу, гортання й кнопок.
 *
 * Ключ — файл, спосіб віддачі й позначка версії (`updatedAt` вузла): після
 * нової версії файлу старе посилання веде на стару версію, тож воно не
 * підходить. Посилання без `expiresAt` не кешуємо.
 */
export class DiskContentUrlCache {
    private readonly entries = new Map<string, Entry>()

    constructor(private readonly now: () => number = () => Date.now()) {}

    static key(uuid: string, disposition: 'inline' | 'attachment', stamp: string | null): string {
        return `${uuid}|${disposition}|${stamp ?? ''}`
    }

    get(key: string): string | null {
        const entry = this.entries.get(key)

        if (!entry) return null
        if (entry.validUntil <= this.now()) {
            this.entries.delete(key)

            return null
        }

        return entry.url
    }

    set(key: string, value: DiskContentUrl): void {
        const expires = value.expiresAt === null ? Number.NaN : Date.parse(value.expiresAt)

        if (!Number.isFinite(expires)) return

        const validUntil = expires - CONTENT_URL_MARGIN_MS

        if (validUntil > this.now()) this.entries.set(key, { url: value.url, validUntil })
    }

    /** Посилання відхилено (403 — строк минув або право забрали): більше не видаємо. */
    forget(key: string): void {
        this.entries.delete(key)
    }

    clear(): void {
        this.entries.clear()
    }
}
